import type { JSX } from 'react';

import { cn } from '../lib/cn';
import { PILL_SHELL } from '../lib/pill-shell';

import type { DomainColor } from './status-badge-domains';

export interface DietaryBadgeProps {
  code: string;
  className?: string;
}

/** Same tinted-fill tokens as `status-badge.tsx`; no new color pair. */
const DIETARY_COLOR_STYLES: Record<DomainColor, string> = {
  success: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-600/20',
  warning: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/20',
  danger: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/20',
  neutral: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-500/20',
};

/**
 * `AvailableMenu.dietaryTags`/`OrderItem.dietaryTags` carry codes, not the
 * readable `name` the back-office catalog has \u2014 this maps the codes seen
 * in `apps/api/prisma/seed-demo.ts`, mirroring `STATUS_BADGE` in
 * `status-badge-domains.ts`.
 */
const DIETARY_LABELS: Record<string, { label: string; color: DomainColor }> = {
  VEGANO: { label: 'Vegano', color: 'success' },
  VEGETARIANO: { label: 'Vegetariano', color: 'success' },
  SIN_TACC: { label: 'Sin TACC', color: 'warning' },
  HIPOSODICO: { label: 'Hiposódico', color: 'neutral' },
  LIVIANO: { label: 'Liviano', color: 'neutral' },
};

/** `NUEVA_ETIQUETA` -> `Nueva etiqueta`: only the first word is capitalized. */
function toFallbackLabel(code: string): string {
  const [first, ...rest] = code.toLowerCase().split('_');
  return [`${first?.charAt(0).toUpperCase()}${first?.slice(1)}`, ...rest].join(' ');
}

/** An unrecognized code falls back to a readable label instead of throwing. */
export function DietaryBadge({ code, className }: DietaryBadgeProps): JSX.Element {
  const entry = DIETARY_LABELS[code] ?? { label: toFallbackLabel(code), color: 'neutral' as const };

  return (
    <span className={cn(PILL_SHELL, DIETARY_COLOR_STYLES[entry.color], className)}>
      {entry.label}
    </span>
  );
}
